import { Suspense } from 'react'
import FeedPage from '@/pages/feed'
import ShopsPage from '@/pages/shops'
import ArenaPage from '@/pages/arena'
import ClipsPage from '@/pages/clips'
import OrdersPage from '@/pages/orders'
import TrendsPage from '@/pages/trends'
import { Role } from '@/types/user.type'
import FeedForm from '@/pages/feed-form'
// Page imports
import Dashboard from '@/pages/dashboard'
import BannersPage from '@/pages/banners'
import ProfilePage from '@/pages/profile'
import ArticlesPage from '@/pages/articles'
import ProgramsPage from '@/pages/programs'
import ProductsPage from '@/pages/products'
import SettingsPage from '@/pages/settings'
import ShopFormPage from '@/pages/shop-form'
import FeedDetail from '@/pages/feed-detail'
import PrivilegePage from '@/pages/privilege'
import ArenaFormPage from '@/pages/arena-form'
import UserVerifyPage from '@/pages/user-verify'
// Layout imports
import DashboardLayout from '@/layouts/dashboard'
import BankPaymentPage from '@/pages/bank-payment'
import ProfileEditPage from '@/pages/profile-form'
import ArticleFormPage from '@/pages/article-form'
import ProgramFormPage from '@/pages/program-form'
import ProductFormPage from '@/pages/product-form'
import ProgramManage from '@/pages/program-manage'
import PrivilegeForm from '@/pages/privilege-form'
import TrendSoundsPage from '@/pages/trend-sounds'
// Context imports
import { Navigate, Outlet } from 'react-router-dom'
import RoosterMatchPage from '@/pages/rooster-match'
import RedeemCondition from '@/pages/redeem-condition'
import TransferTokenPage from '@/pages/transfer-token'
import ClipCategoriesPage from '@/pages/clip-categories'
import OrderPrivilegePage from '@/pages/order-privilege'
import TrendSoundFormPage from '@/pages/trend-sound-form'
import { AuthGuard } from '@/contexts/auth-context/guards/auth-guard'
import { RoleGuard } from '@/contexts/auth-context/guards/role-guard'
// Component imports
import LoadingSection from '@/components/loading-indicator/loading-section'

export const protectedRoutes = [
  {
    path: '',
    element: (
      <AuthGuard>
        <DashboardLayout>
          <Suspense fallback={<LoadingSection loading />}>
            <Outlet />
          </Suspense>
        </DashboardLayout>
      </AuthGuard>
    ),
    children: [
      // Dashboard - ทุก role เข้าถึงได้
      {
        path: 'dashboard',
        element: <Dashboard />,
      },

      // Profile - ทุก role เข้าถึงได้
      {
        path: 'profile',
        element: <ProfilePage />,
      },
      {
        path: 'profile/edit',
        element: <ProfileEditPage />,
      },

      // Products module - SHOP only
      {
        path: 'products',
        element: (
          <RoleGuard allowedRoles={[Role.SHOP]}>
            <ProductsPage />
          </RoleGuard>
        ),
      },
      {
        path: 'products/create',
        element: (
          <RoleGuard allowedRoles={[Role.SHOP]}>
            <ProductFormPage />
          </RoleGuard>
        ),
      },
      {
        path: 'products/:id/edit',
        element: (
          <RoleGuard allowedRoles={[Role.SHOP]}>
            <ProductFormPage />
          </RoleGuard>
        ),
      },

      // Orders module - SHOP only
      {
        path: 'orders',
        element: (
          <RoleGuard allowedRoles={[Role.ADMIN, Role.SHOP]}>
            <OrdersPage />
          </RoleGuard>
        ),
      },

      // Feed module - All users
      {
        path: 'feeds',
        element: (
          <RoleGuard allowedRoles={[Role.ADMIN, Role.SHOP]}>
            <FeedPage />
          </RoleGuard>
        ),
      },

      // Feed module - ADMIN only
      {
        path: 'feeds/:id/detail',
        element: (
          <RoleGuard allowedRoles={[Role.ADMIN]}>
            <FeedDetail />
          </RoleGuard>
        ),
      },

      // Feed module - ADMIN only
      {
        path: 'feeds/create',
        element: (
          <RoleGuard allowedRoles={[Role.ADMIN]}>
            <FeedForm />
          </RoleGuard>
        ),
      },

      // Feed module - ADMIN only
      {
        path: 'feeds/:id/edit',
        element: (
          <RoleGuard allowedRoles={[Role.ADMIN]}>
            <FeedForm />
          </RoleGuard>
        ),
      },

      // Articles module - ADMIN only
      {
        path: 'articles',
        element: (
          <RoleGuard allowedRoles={[Role.ADMIN]}>
            <ArticlesPage />
          </RoleGuard>
        ),
      },
      {
        path: 'articles/create',
        element: (
          <RoleGuard allowedRoles={[Role.ADMIN]}>
            <ArticleFormPage />
          </RoleGuard>
        ),
      },
      {
        path: 'articles/:id/edit',
        element: (
          <RoleGuard allowedRoles={[Role.ADMIN]}>
            <ArticleFormPage />
          </RoleGuard>
        ),
      },

      // Shops module - ADMIN only
      {
        path: 'shops',
        element: (
          <RoleGuard allowedRoles={[Role.ADMIN]}>
            <ShopsPage />
          </RoleGuard>
        ),
      },
      {
        path: 'shops/create',
        element: (
          <RoleGuard allowedRoles={[Role.ADMIN]}>
            <ShopFormPage />
          </RoleGuard>
        ),
      },
      {
        path: 'shops/:id/edit',
        element: (
          <RoleGuard allowedRoles={[Role.ADMIN]}>
            <ShopFormPage />
          </RoleGuard>
        ),
      },

      // Programs module - ADMIN only
      {
        path: 'programs',
        element: (
          <RoleGuard allowedRoles={[Role.ADMIN]}>
            <ProgramsPage />
          </RoleGuard>
        ),
      },
      {
        path: 'programs/create',
        element: (
          <RoleGuard allowedRoles={[Role.ADMIN]}>
            <ProgramFormPage />
          </RoleGuard>
        ),
      },
      {
        path: 'programs/:id/edit',
        element: (
          <RoleGuard allowedRoles={[Role.ADMIN]}>
            <ProgramFormPage />
          </RoleGuard>
        ),
      },
      {
        path: 'programs/:id/manage',
        element: (
          <RoleGuard allowedRoles={[Role.ADMIN]}>
            <ProgramManage />
          </RoleGuard>
        ),
      },
      {
        path: 'banners',
        element: (
          <RoleGuard allowedRoles={[Role.ADMIN]}>
            <BannersPage />
          </RoleGuard>
        ),
      },
      {
        path: 'user-verify',
        element: (
          <RoleGuard allowedRoles={[Role.ADMIN]}>
            <UserVerifyPage />
          </RoleGuard>
        ),
      },
      {
        path: 'settings',
        element: (
          <RoleGuard allowedRoles={[Role.ADMIN]}>
            <SettingsPage />
          </RoleGuard>
        ),
      },
      // QR Code page - ทุก role เข้าถึงได้
      {
        path: 'bank-payment',
        element: (
          <RoleGuard allowedRoles={[Role.ADMIN]}>
            <BankPaymentPage />
          </RoleGuard>
        ),
      },

      // Privilege module - ADMIN only
      {
        path: 'privileges',
        element: (
          <RoleGuard allowedRoles={[Role.ADMIN]}>
            <PrivilegePage />
          </RoleGuard>
        ),
      },
      {
        path: 'privileges/create',
        element: (
          <RoleGuard allowedRoles={[Role.ADMIN]}>
            <PrivilegeForm />
          </RoleGuard>
        ),
      },
      {
        path: 'privileges/:id/edit',
        element: (
          <RoleGuard allowedRoles={[Role.ADMIN]}>
            <PrivilegeForm />
          </RoleGuard>
        ),
      },
      {
        path: 'privileges/:id/redeem-condition',
        element: (
          <RoleGuard allowedRoles={[Role.ADMIN]}>
            <RedeemCondition />
          </RoleGuard>
        ),
      },
      {
        path: 'order-privileges',
        element: (
          <RoleGuard allowedRoles={[Role.ADMIN]}>
            <OrderPrivilegePage />
          </RoleGuard>
        ),
      },

      // Rooster match module - ADMIN only
      {
        path: 'rooster-match',
        element: (
          <RoleGuard allowedRoles={[Role.ADMIN]}>
            <RoosterMatchPage />
          </RoleGuard>
        ),
      },

      // Arena module - ADMIN only
      {
        path: 'arenas',
        element: (
          <RoleGuard allowedRoles={[Role.ADMIN]}>
            <ArenaPage />
          </RoleGuard>
        ),
      },
      {
        path: 'arenas/create',
        element: (
          <RoleGuard allowedRoles={[Role.ADMIN]}>
            <ArenaFormPage />
          </RoleGuard>
        ),
      },
      {
        path: 'arenas/:id/edit',
        element: (
          <RoleGuard allowedRoles={[Role.ADMIN]}>
            <ArenaFormPage />
          </RoleGuard>
        ),
      },
      {
        path: 'transfer-token',
        element: (
          <RoleGuard allowedRoles={[Role.ADMIN]}>
            <TransferTokenPage />
          </RoleGuard>
        ),
      },

      // Trend Sounds module - ADMIN only
      {
        path: 'trend-sounds',
        element: (
          <RoleGuard allowedRoles={[Role.ADMIN]}>
            <TrendSoundsPage />
          </RoleGuard>
        ),
      },
      {
        path: 'trend-sounds/create',
        element: (
          <RoleGuard allowedRoles={[Role.ADMIN]}>
            <TrendSoundFormPage />
          </RoleGuard>
        ),
      },
      {
        path: 'trend-sounds/:id/edit',
        element: (
          <RoleGuard allowedRoles={[Role.ADMIN]}>
            <TrendSoundFormPage />
          </RoleGuard>
        ),
      },

      // Trends module - ADMIN only
      {
        path: 'trends',
        element: (
          <RoleGuard allowedRoles={[Role.ADMIN]}>
            <TrendsPage />
          </RoleGuard>
        ),
      },

      // Clip categories - ADMIN only
      {
        path: 'clip-categories',
        element: (
          <RoleGuard allowedRoles={[Role.ADMIN]}>
            <ClipCategoriesPage />
          </RoleGuard>
        ),
      },

      // Clips module - ADMIN only
      {
        path: 'clips',
        element: (
          <RoleGuard allowedRoles={[Role.ADMIN]}>
            <ClipsPage />
          </RoleGuard>
        ),
      },
      {
        path: 'clips/create',
        element: (
          <RoleGuard allowedRoles={[Role.ADMIN]}>
            <Navigate to="/clips" replace />
          </RoleGuard>
        ),
      },
      {
        path: 'clips/:id/edit',
        element: (
          <RoleGuard allowedRoles={[Role.ADMIN]}>
            <ClipsPage />
          </RoleGuard>
        ),
      },
    ],
  },
]
